import { useEffect, useState } from "react";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { ArrowRightLeft, ExternalLink, Trash2, Upload } from "lucide-react";
import type { Match } from "@/lib/types";
import { matchHref } from "../matchLinks";
import "../match-actions.css";

type MatchActionsProps = {
  match: Match;
  onMedia?: () => void;
  onEdit: () => void;
  onUpload: () => void;
  onDelete?: () => void;
  onMove?: () => void;
  testIdPrefix: string;
};

/** Shared operational controls; permissions are supplied by the caller. */
export function MatchActions(props: MatchActionsProps) {
  const { match, onMedia, onEdit, testIdPrefix } = props;
  return (
    <div className="match-row-actions" data-match-actions={match.id}>
      {onMedia ? (
        <button
          type="button"
          className="match-row-primary"
          data-testid={`${testIdPrefix}-expand`}
          aria-label="Media slots"
          onClick={onMedia}
        >
          Media slots
        </button>
      ) : null}
      <button
        type="button"
        data-testid={`${testIdPrefix}-edit`}
        aria-label="Edit"
        onClick={onEdit}
      >
        Edit
      </button>
      <MatchRowMenu {...props} />
    </div>
  );
}

function MatchRowMenu({
  match,
  onUpload,
  onDelete,
  onMove,
  onMedia,
  testIdPrefix,
}: MatchActionsProps) {
  const [open, setOpen] = useState(false);
  // A replaced row must not leave actions operating on its old snapshot.
  useEffect(() => setOpen(false), [match.updated_at]);
  return (
    <Menu.Root modal={false} open={open} onOpenChange={setOpen}>
      <Menu.Trigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-more`}
          aria-label={`More options for ${match.home_team} vs ${match.away_team}`}
        >
          More
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          className="match-row-menu"
          sideOffset={6}
          align="end"
          collisionPadding={12}
          updatePositionStrategy="always"
          onClick={(event) => event.stopPropagation()}
        >
          <Menu.Item asChild>
            <a
              href={matchHref(match)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}-open`}
            >
              <ExternalLink size={17} />
              Open match
            </a>
          </Menu.Item>
          <Menu.Item
            onSelect={onUpload}
            data-testid={`${testIdPrefix}-${onMedia ? "upload" : "upload-menu"}`}
          >
            <Upload size={17} />
            Upload video
          </Menu.Item>
          {onMove ? (
            <Menu.Item onSelect={onMove} data-testid={`${testIdPrefix}-move`}>
              <ArrowRightLeft size={17} />
              Move to squad
            </Menu.Item>
          ) : null}
          {onDelete ? (
            <Menu.Item
              onSelect={onDelete}
              className="match-row-menu-danger"
              data-testid={`${testIdPrefix}-delete`}
            >
              <Trash2 size={17} />
              Delete match
            </Menu.Item>
          ) : null}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
